// usePermission hook - permission-key-based access control
import { useMe } from '../auth/hooks/useMe'
import type { Permission } from '../types'

/**
 * Maps backend permission keys to frontend permission names
 */
const PERMISSION_KEY_MAP: Record<string, Permission> = {
  'dashboard.view': 'view_dashboard',
  'products.search': 'search_products',
  'products.view': 'view_products',
  'orders.place': 'place_orders',
  'orders.view_own': 'view_own_orders',
  'approvals.view': 'view_approvals',
  'approvals.action': 'approve_orders',
  'orders.view_all': 'view_all_orders',
  'users.view': 'view_users',
  'users.manage': 'manage_users',
  'settings.view': 'view_settings',
  'settings.edit': 'manage_settings',
}

/**
 * Permission check hook
 * Uses the permissions[] array returned by /api/users/me
 */
export function usePermission() {
  const { data: me } = useMe()
  const backendPermissions = me?.permissions ?? []

  /**
   * Check if the current user has a specific permission
   */
  function can(permission: Permission): boolean {
    const matchingKeys = Object.entries(PERMISSION_KEY_MAP)
      .filter(([_, frontendPerm]) => frontendPerm === permission)
      .map(([backendKey]) => backendKey)
    return matchingKeys.some(key => backendPermissions.includes(key))
  }

  /**
   * Check if the current user has any of the given permissions
   */
  function canAny(...permissions: Permission[]): boolean {
    return permissions.some(p => can(p))
  }

  /**
   * Check if the current user has all of the given permissions
   */
  function canAll(...permissions: Permission[]): boolean {
    return permissions.every(p => can(p))
  }

  return {
    can,
    canAny,
    canAll,
    isAdmin: me?.role === 'admin',
    isApprover: me?.role === 'approver',
    isMember: me?.role === 'member',
  }
}
